class NonomicPopup extends HTMLElement {
  static SESSION_KEY = 'nonomic_popup_shown';
  static FORMULA_CLICKED_KEY = 'nonomic_formula_clicked';
  static COOKIE_DISMISSED = 'nonomic_popup_dismissed';
  static COOKIE_SUBSCRIBED = 'nonomic_subscribed';

  constructor() {
    super();
    this.opened = false;
    this.entering = false;
    this.shown = false;
    this.scrollMet = false;
    this.timeMet = false;
    this.timerId = null;
    this.boundScroll = this.onScroll.bind(this);
    this.boundExitIntent = this.onExitIntent.bind(this);
    this.boundCtaClick = this.onCtaClick.bind(this);
    this.boundBackdropClick = this.onBackdropClick.bind(this);
    this.boundKeyup = this.onKeyup.bind(this);
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);

    if (this.dataset.enabled !== 'true') return;

    this.backdrop = this.querySelector('[data-nonomic-popup-backdrop]');
    this.dialog = this.querySelector('[data-nonomic-popup-dialog]');
    this.formView = this.querySelector('[data-nonomic-popup-form-view]');
    this.successView = this.querySelector('[data-nonomic-popup-success-view]');
    this.form = this.querySelector('.nonomic-popup__form');

    this.bindUi();
    this.initTriggers();

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:select', this.onSectionSelect.bind(this));
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect.bind(this));
    }
  }

  disconnectedCallback() {
    this.teardownTriggers();
    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
    }
  }

  get isTestMode() {
    return this.dataset.testMode === 'true';
  }

  get config() {
    return {
      delayMs: Number(this.dataset.delayMs) || 35000,
      scrollThreshold: Number(this.dataset.scrollThreshold) || 55,
      dismissCookieDays: Number(this.dataset.dismissCookieDays) || 7,
      suppressedPaths: (this.dataset.suppressedPaths || '')
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean),
      formulaCtaText: this.dataset.formulaCtaText || 'Choose Your Formula',
      enableExitIntent: this.dataset.exitIntent === 'true',
      enableScrollTrigger: this.dataset.scrollTrigger === 'true',
      enableTimeTrigger: this.dataset.timeTrigger === 'true',
      mobileMaxWidth: Number(this.dataset.mobileMaxWidth) || 749,
    };
  }

  bindUi() {
    this.querySelectorAll('[data-nonomic-popup-close]').forEach((el) => {
      el.addEventListener('click', () => this.dismiss());
    });

    if (this.backdrop) {
      this.backdrop.addEventListener('click', this.boundBackdropClick);
    }

    this.addEventListener('keyup', this.boundKeyup);

    if (this.form) {
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }
  }

  onBackdropClick(event) {
    if (event.target === this.backdrop) this.dismiss();
  }

  onKeyup(event) {
    if (event.code === 'Escape' && this.opened) this.dismiss();
  }

  initTriggers() {
    if (this.shouldBlock()) return;

    const { enableScrollTrigger, enableTimeTrigger, enableExitIntent, delayMs, mobileMaxWidth } =
      this.config;

    if (enableTimeTrigger) {
      this.timerId = window.setTimeout(() => {
        this.timeMet = true;
        this.tryShow();
      }, delayMs);
    } else {
      this.timeMet = true;
    }

    if (enableScrollTrigger) {
      window.addEventListener('scroll', this.boundScroll, { passive: true });
    } else {
      this.scrollMet = true;
    }

    const isMobile = window.matchMedia(`(max-width: ${mobileMaxWidth}px)`).matches;
    if (enableExitIntent && !isMobile) {
      document.addEventListener('mouseleave', this.boundExitIntent);
    }

    document.addEventListener('click', this.boundCtaClick);

    if (!enableScrollTrigger && !enableTimeTrigger) {
      this.scrollMet = true;
      this.timeMet = true;
      this.tryShow();
    }
  }

  teardownTriggers() {
    if (this.timerId) window.clearTimeout(this.timerId);
    window.removeEventListener('scroll', this.boundScroll);
    document.removeEventListener('mouseleave', this.boundExitIntent);
    document.removeEventListener('click', this.boundCtaClick);
  }

  isPageAllowed() {
    if (typeof Shopify !== 'undefined' && Shopify.designMode) return true;

    const template = this.dataset.template;
    if (template === 'index' && this.dataset.showOnHomepage === 'true') return true;
    if (template === 'product' && this.dataset.showOnProduct === 'true') return true;
    return false;
  }

  shouldBlock() {
    if (!this.isPageAllowed()) return true;

    const path = window.location.pathname;
    const { suppressedPaths } = this.config;

    if (suppressedPaths.some((prefix) => prefix && path.startsWith(prefix))) return true;
    if (sessionStorage.getItem(NonomicPopup.SESSION_KEY)) return true;
    if (sessionStorage.getItem(NonomicPopup.FORMULA_CLICKED_KEY)) return true;

    if (!this.isTestMode) {
      if (this.getCookie(NonomicPopup.COOKIE_DISMISSED)) return true;
      if (this.getCookie(NonomicPopup.COOKIE_SUBSCRIBED)) return true;
    }

    return false;
  }

  onScroll() {
    const total = document.documentElement.scrollHeight - window.innerHeight;
    if (total <= 0) return;

    const ratio = (window.scrollY / total) * 100;
    if (ratio >= this.config.scrollThreshold) {
      this.scrollMet = true;
      this.tryShow();
    }
  }

  onExitIntent(event) {
    if (event.clientY <= 5) this.show();
  }

  onCtaClick(event) {
    const el = event.target.closest('button, a');
    if (!el?.textContent?.includes(this.config.formulaCtaText)) return;
    sessionStorage.setItem(NonomicPopup.FORMULA_CLICKED_KEY, '1');
  }

  tryShow() {
    if (!this.scrollMet || !this.timeMet) return;
    this.show();
  }

  show(options = {}) {
    const force = options.force === true;

    if (!force && (this.shown || this.shouldBlock())) return;
    if (force) {
      this.shown = false;
      this.resetViews();
    }

    this.shown = true;
    sessionStorage.setItem(NonomicPopup.SESSION_KEY, '1');
    this.teardownTriggers();

    this.opened = true;
    this.removeAttribute('hidden');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.entering = true;
        this.classList.add('is-open', 'is-entering');
        this.dialog?.focus();
      });
    });
  }

  dismiss() {
    if (!this.opened) return;

    if (!this.isTestMode) {
      this.setCookie(NonomicPopup.COOKIE_DISMISSED, 'true', this.config.dismissCookieDays);
    }
    this.closeWithAnimation();
  }

  showSuccess() {
    if (!this.isTestMode) {
      this.setCookie(NonomicPopup.COOKIE_SUBSCRIBED, 'true');
    }
    if (this.formView) this.formView.hidden = true;
    if (this.successView) this.successView.hidden = false;

    const successDelay = Number(this.dataset.successDelayMs) || 1800;
    window.setTimeout(() => this.closeWithAnimation(), successDelay);
  }

  closeWithAnimation() {
    this.entering = false;
    this.classList.remove('is-entering');

    window.setTimeout(() => {
      this.opened = false;
      this.shown = false;
      this.classList.remove('is-open');
      this.setAttribute('hidden', '');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
    }, 420);
  }

  resetViews() {
    if (this.formView) this.formView.hidden = false;
    if (this.successView) this.successView.hidden = true;
    if (this.form) this.form.reset();
    const errorEl = this.form?.querySelector('[data-nonomic-popup-error]');
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    const emailInput = this.form?.querySelector('[name="contact[email]"]');
    const email = emailInput?.value?.trim();
    if (!email) return;

    const errorEl = this.form?.querySelector('[data-nonomic-popup-error]');
    if (errorEl) {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    const submitBtn = this.form?.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    try {
      const response = await fetch('/contact', {
        method: 'POST',
        body: new FormData(this.form),
        headers: { Accept: 'text/html' },
      });

      const html = await response.text();
      const hasError =
        html.includes('form__message--error') ||
        (html.includes('field__input--error') && html.includes('email'));

      if (hasError) {
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent =
            this.dataset.errorMessage || 'Please enter a valid email address.';
        }
        return;
      }

      this.showSuccess();
    } catch {
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.textContent = this.dataset.errorMessage || 'Something went wrong. Please try again.';
      }
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  onSectionSelect(event) {
    if (event.detail.sectionId !== this.dataset.sectionId) return;
    this.show({ force: true });
  }

  onSectionDeselect(event) {
    if (event.detail.sectionId !== this.dataset.sectionId) return;
    this.closeWithAnimation();
  }

  getCookie(name) {
    return document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${name}=`))
      ?.split('=')[1];
  }

  setCookie(name, value, days) {
    let expires = '';
    if (days !== undefined) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = `; expires=${date.toUTCString()}`;
    }
    document.cookie = `${name}=${value}${expires}; path=/; SameSite=Lax`;
  }
}

customElements.define('nonomic-popup', NonomicPopup);
